import React, { useState } from "react";
import { clientsApi } from "../../../services";
import type { Client } from "../../../types";
import { Modal, Input, FormActions, Icons } from "../../shared";
import {
  validatePhoneNumber,
  handlePhoneInputChange,
  cleanPhoneNumber,
  formatPhoneNumber,
} from "../../../utils/phoneUtils";
import {
  cleanCpf,
  formatCpf,
  handleCpfInputChange,
  validateCpf,
} from "../../../utils/cpfUtils";
import {
  applyBirthdayMask,
  validateBirthday,
  convertBirthdayToAPIFormat,
} from "../../../utils/dateUtils";
import { ClientFormData } from "../../../services/clientsApi";

interface CreateClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  client?: Client | null;
  onSuccess?: (createdClient: Client) => void;
}

export const CreateClientModal: React.FC<CreateClientModalProps> = ({
  isOpen,
  onClose,
  client,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<ClientFormData>({
    name: client?.name || "",
    phoneNumber: client?.phoneNumber || "",
    birthday: client?.birthday || "",
    address: client?.address || "",
    cpf: client?.cpf || "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [birthdayError, setBirthdayError] = useState<string | null>(null);
  const [cpfError, setCpfError] = useState<string | null>(null);

  const isEditing = Boolean(client);

  // Convert YYYY-MM-DD to DD/MM/YYYY for display
  const convertBirthdayFromAPIFormat = (value: string): string => {
    if (!value) return "";
    const dateRegex = /^(\d{4})-(\d{2})-(\d{2})$/;
    const match = value.match(dateRegex);
    if (match) {
      const year = match[1];
      const month = match[2];
      const day = match[3];
      return `${day}/${month}/${year}`;
    }
    return value;
  };

  React.useEffect(() => {
    if (client) {
      setFormData({
        name: client.name,
        phoneNumber: client.phoneNumber
          ? formatPhoneNumber(client.phoneNumber)
          : "",
        birthday: client.birthday ? convertBirthdayFromAPIFormat(client.birthday) : "",
        address: client.address || "",
        cpf: client.cpf ? formatCpf(client.cpf) : "",
      });
    } else {
      setFormData({
        name: "",
        phoneNumber: "",
        birthday: "",
        address: "",
        cpf: "",
      });
    }
    setError(null);
    setBirthdayError(null);
    setCpfError(null);
  }, [client]);

  const handleInputChange = (field: keyof ClientFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (error) setError(null);
  };

  const handlePhoneChange = (value: string) => {
    handlePhoneInputChange(value, (formatted) =>
      handleInputChange("phoneNumber", formatted)
    );
  };

  const handleBirthdayChange = (value: string) => {
    const maskedValue = applyBirthdayMask(value);
    handleInputChange("birthday", maskedValue);
    if (birthdayError) setBirthdayError(null);
  };

  const handleCpfChange = (value: string) => {
    handleCpfInputChange(value, (formatted) => handleInputChange("cpf", formatted));
    if (cpfError) setCpfError(null);
  };

  // Capitalize first letter of each word
  const capitalizeName = (name: string): string => {
    return name
      .trim()
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const validateForm = (): boolean => {
    setError(null);
    setCpfError(null);
    setBirthdayError(null);

    if (!formData.name.trim()) {
      setError("Nome é obrigatório");
      return false;
    }

    // Phone number is optional, but if provided, it must be valid
    if (formData.phoneNumber && formData.phoneNumber.trim()) {
      const phoneValidation = validatePhoneNumber(formData.phoneNumber);
      if (!phoneValidation.isValid) {
        setError(phoneValidation.error || "Telefone inválido");
        return false;
      }
    }

    if (formData.cpf && formData.cpf.trim()) {
      const cpfValidation = validateCpf(formData.cpf);
      if (!cpfValidation.isValid) {
        setCpfError(cpfValidation.error || "CPF inválido");
        return false;
      }
    }

    if (formData.birthday && formData.birthday.trim()) {
      const birthdayValidation = validateBirthday(formData.birthday);
      if (!birthdayValidation.isValid) {
        setBirthdayError(birthdayValidation.error || "Data inválida");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      // Clean phone number and convert birthday format before submitting
      const cleanedFormData: ClientFormData = {
        name: capitalizeName(formData.name),
        ...(formData.phoneNumber && formData.phoneNumber.trim() && {
          phoneNumber: cleanPhoneNumber(formData.phoneNumber),
        }),
        ...(formData.birthday && {
          birthday: convertBirthdayToAPIFormat(formData.birthday),
        }),
        ...(isEditing
          ? { address: formData.address?.trim() || "" }
          : formData.address?.trim() && { address: formData.address.trim() }),
        ...(isEditing
          ? { cpf: formData.cpf?.trim() ? cleanCpf(formData.cpf) : "" }
          : formData.cpf?.trim() && { cpf: cleanCpf(formData.cpf) }),
      };

      const response =
        isEditing && client
          ? await clientsApi.updateClient(client.id, cleanedFormData)
          : await clientsApi.createClient(cleanedFormData);

      if (response.success && response.data) {
        if (onSuccess && !isEditing) {
          onSuccess(response.data);
        }
        onClose();
      } else {
        setError(
          response.error ||
            `Failed to ${isEditing ? "update" : "create"} client`
        );
      }
    } catch (error) {
      console.error(
        `Error ${isEditing ? "updating" : "creating"} client:`,
        error
      );
      setError(
        error instanceof Error
          ? error.message
          : `Failed to ${isEditing ? "update" : "create"} client`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleButtonSubmit = () => {
    handleSubmit();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? "Editar Cliente" : "Novo Cliente"}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          {/* Name */}
          <div>
            <Input
              label="Nome"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Digite o nome do cliente"
              required
            />
          </div>

          {/* Phone */}
          <div>
            <Input
              label="Telefone"
              type="tel"
              value={formData.phoneNumber || "" as string}
              onChange={(e) => handlePhoneChange(e.target.value as string)}
              onKeyDown={handleKeyPress}
              placeholder="(11) 99999-9999"
            />
          </div>

          {/* CPF */}
          <div>
            <Input
              type="text"
              name="cpf"
              value={formData.cpf || ""}
              onChange={(e) => handleCpfChange(e.target.value)}
              onBlur={(e) => {
                const validation = validateCpf(e.target.value);
                if (!validation.isValid && e.target.value.trim()) {
                  setCpfError(validation.error || "CPF inválido");
                } else {
                  setCpfError(null);
                }
              }}
              onKeyDown={handleKeyPress}
              placeholder="000.000.000-00"
              error={cpfError || undefined}
              leadingIcon={
                <Icons.Badge sx={{ fontSize: 20 }} titleAccess="CPF" />
              }
            />
          </div>

          {/* Birthday */}
          <div>
            <Input
              label="Data de Nascimento"
              type="text"
              value={formData.birthday || ""}
              onChange={(e) => handleBirthdayChange(e.target.value)}
              onBlur={(e) => {
                // Validate on blur
                const validation = validateBirthday(e.target.value);
                if (!validation.isValid && e.target.value.trim()) {
                  setBirthdayError(validation.error || "Data inválida");
                } else if (validation.isValid) {
                  setBirthdayError(null);
                }
              }}
              onKeyDown={handleKeyPress}
              placeholder="DD/MM/AAAA"
              error={birthdayError || undefined}
            />
          </div>

          {/* Address */}
          <div>
            <Input
              label="Endereço"
              type="text"
              value={formData.address || ""}
              onChange={(e) => handleInputChange("address", e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Digite o endereço do cliente"
            />
          </div>
        </div>

        <FormActions
          onCancel={onClose}
          onSubmit={handleButtonSubmit}
          loading={loading}
          isEditing={isEditing}
          submitText={isEditing ? "Salvar Alterações" : "Criar Cliente"}
          submitType="button"
        />
      </form>
    </Modal>
  );
};
